import * as React from 'react';
import * as Images from 'pos/Images';
import { CustomerDisplayConfigurationDTO, SaleDTO, ScreenSaverType, TicketDTO, TicketLineDTO } from 'core/api';
import MessageControl from './MessageControl';
import AmountControl from './AmountControl';
import TicketControl from './TicketControl';
import SaleContent from './SaleControl';
import WeightControl from './WeightControl';
import { RestaurantFacade } from 'pos/facades';
import { createRoot } from 'react-dom/client';
import { Flex } from 'core/ui';

type Message = {
	message: string;
	type: 'Message';
};

type Amount = {
	amount: number;
	type: 'Amount';
};

type Ticket = {
	ticket: TicketDTO;
	type: 'Ticket';
};

type Sale = {
	sale: SaleDTO;
	type: 'Sale';
};

type TicketWithLine = {
	ticket: TicketDTO;
	line: TicketLineDTO;
	type: 'TicketWithLine';
};

type Weight = {
	kgs: number,
	pricePerKgs: number;
	total: number;
	type: 'Weight';
};

type ScreenSaverOptions = {
	enabled: boolean;
	image: Nullable<string>;
	waitTimeInSeconds: number;
	type: ScreenSaverType;
	url: string;
};

type Props = {
	image: string; // Base64
	showLineNotes: boolean;
	screenSaverOptions: ScreenSaverOptions;
}

export type ScreenContentState = Message | Amount | Ticket | TicketWithLine | Weight | Sale;

type State = {
	content: ScreenContentState,
	showScreenSaver: boolean
};

function ScreenContent({ content, showLineNotes }: { content: ScreenContentState, showLineNotes: boolean }): React.ReactNode {
	switch (content.type) {
		case 'Message':
			return <MessageControl message={content.message} />;
		case 'Amount':
			return <AmountControl amount={content.amount} />;
		case 'Ticket':
			return <TicketControl ticket={content.ticket} showLineNotes={showLineNotes} />;
		case 'TicketWithLine':
			return <TicketControl ticket={content.ticket} selectedLine={content.line} showLineNotes={showLineNotes} />;
		case 'Sale':
			return <SaleContent sale={content.sale} />;
		case 'Weight':
			return <WeightControl kgs={content.kgs} pricePerKgs={content.pricePerKgs} total={content.total} />;
	}
}

let screen: Screen | null = null;

export class Screen extends React.Component<Props, State> {
	private timerId: Nullable<NodeJS.Timeout> = null;
	private lastContentUpdate: Date = new Date();

	constructor(props: Props) {
		super(props);

		this.state = {
			content: {
				type: 'Message',
				message: '',
			},
			showScreenSaver: false,
		};
	}

	componentDidMount() {
		this.timerId = this.props.screenSaverOptions.enabled ? setInterval(() => {
			const currentTime = new Date();
			const diffInSeconds = (currentTime.getTime() - this.lastContentUpdate.getTime()) / 1000;
			if (diffInSeconds > this.props.screenSaverOptions.waitTimeInSeconds) {
				this.setState({ showScreenSaver: true });
			}
		}, 1000) : null;
		screen = this;
	}

	componentWillUnmount() {
		screen = null;
		this.timerId && clearInterval(this.timerId);
	}

	setContent(content: ScreenContentState) {
		this.lastContentUpdate = new Date();
		this.setState({ content, showScreenSaver: false });
	}

	render(): React.ReactNode {
		return <div className='legacy-styles'>{
			this.state.showScreenSaver
				? <div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						backgroundColor: 'black',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 9999,
					}}>
					{
						<ScreenSaverMedia type={this.props.screenSaverOptions.type} image={this.props.screenSaverOptions.image} url={this.props.screenSaverOptions.url} />
					}
				</div>
				: <Flex.Horizontal>
					<Flex.Item size='68.4ex' className='screen-display'>
						<ScreenContent content={this.state.content} showLineNotes={this.props.showLineNotes} />
					</Flex.Item>
					<Flex.Item size='*' style={{ backgroundColor: '#ec133d', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
						<img src={this.props.image} style={{ minWidth: '100%', minHeight: '100%', maxHeight: '100%', maxWidth: '100%' }} />
					</Flex.Item>
				</Flex.Horizontal>}
		</div>;
	}
}

type ScreenSaverMediaProps = {
	type: ScreenSaverType;
	url: string;
	image: Nullable<string>;
}

function ScreenSaverMedia(props: ScreenSaverMediaProps): React.ReactNode {
	switch (props.type) {
		case ScreenSaverType.DBImage:
			return <img src={props.image ?? undefined} style={{ minWidth: '100%', minHeight: '100%', maxHeight: '100%', maxWidth: '100%' }} />;
		case ScreenSaverType.VideoURL:
			return <video width={'100%'} height={'100%'} controls={false} autoPlay={true} loop={true} muted={true} disableRemotePlayback>
				<source src={props.url} type='video/mp4' />
			</video>;
		case ScreenSaverType.ImageURL:
			return <img src={props.url} style={{ minWidth: '100%', minHeight: '100%', maxHeight: '100%', maxWidth: '100%' }} />;
	}
}


export async function showScreen(configuration: CustomerDisplayConfigurationDTO): Promise<Screen> {
	const image = configuration.ScreenImageId === Images.NULL_IMAGE
		? Images.AGORA_LOGO_SQUARE
		: `data:image/*;base64, ${(await RestaurantFacade.getCustomerDisplayImage(configuration.ScreenImageId)).Content}`;

	const screenSaverImage = configuration.ShowScreenSaver && configuration.ScreenSaverImageId !== Images.NULL_IMAGE
		? `data:image/*;base64, ${(await RestaurantFacade.getCustomerDisplayImage(configuration.ScreenSaverImageId)).Content}`
		: null;

	const screenSaverOptions: ScreenSaverOptions = {
		enabled: configuration.ShowScreenSaver,
		image: screenSaverImage,
		waitTimeInSeconds: configuration.ScreenSaverWaitTimeInSeconds,
		type: configuration.ScreenSaverType,
		url: configuration.ScreenSaverUrl,
	};

	return new Promise((resolve, reject) => {

		const childWindow = window.open('', 'customer-display');
		if (!childWindow) {
			reject('No se pudo crear la ventana para el display');
			return;
		}

		const base = childWindow.document.createElement('base');
		base.href = document.location.origin;
		childWindow.document.head.appendChild(base);

		Array.from(document.styleSheets).forEach(styleSheet => {
			if (styleSheet.cssRules) { // true for inline styles
				const newStyleEl = document.createElement('style');

				Array.from(styleSheet.cssRules).forEach(cssRule => {
					newStyleEl.appendChild(document.createTextNode(cssRule.cssText));
				});

				childWindow?.document.head.appendChild(newStyleEl);
			} else if (styleSheet.href) { // true for stylesheets loaded from a URL
				const newLinkEl = document.createElement('link');

				newLinkEl.rel = 'stylesheet';
				newLinkEl.href = styleSheet.href;
				childWindow?.document.head.appendChild(newLinkEl);
			}
		});

		const containerElement = childWindow.document.createElement('div');
		childWindow.document.body.appendChild(containerElement!);


		const root = createRoot(containerElement!);

		function ScreenWithCallback() {
			React.useEffect(() => {
				resolve(screen!);
			});

			return <Screen image={image} showLineNotes={configuration.ScreenShowLineNotes} screenSaverOptions={screenSaverOptions} />;
		}

		root.render(<ScreenWithCallback />);
	});
}

